/**
 * User context — session token approach.
 *
 * Only the session token is stored in sessionStorage (key: "fso_tok").
 * No user object, no role, no email is ever written to browser storage.
 * A logged-out visitor looking at sessionStorage sees nothing useful.
 *
 * On mount we load the stored token and re-validate it against /api/auth/me.
 * If the server returns 401 (expired cold-start flush) the token is discarded.
 */

import {
  createContext, useContext, useState, useCallback, useEffect, useRef,
  type ReactNode,
} from "react";
import type { User } from "@shared/schema";
import { apiRequest, setActiveToken, registerUnauthHandler, unregisterUnauthHandler } from "@/lib/queryClient";
import { queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";

const TOKEN_KEY = "fso_tok";

function readStoredToken(): string | null {
  try { return sessionStorage.getItem(TOKEN_KEY); }
  catch { return null; }
}

interface UserContextType {
  currentUser: User | null;
  token: string | null;
  /** Call with the token returned from POST /api/auth/login */
  loginWithToken: (token: string, user: User) => void;
  logout: () => void;
  /** True while we're re-validating a stored token on first mount */
  hydrating: boolean;
}

const UserContext = createContext<UserContextType>({
  currentUser: null,
  token: null,
  loginWithToken: () => {},
  logout: () => {},
  hydrating: true,
});

export function UserProvider({ children }: { children: ReactNode }) {
  const [token, setToken] = useState<string | null>(null);
  const [currentUser, setUser] = useState<User | null>(null);
  const [hydrating, setHydrating] = useState(true);
  const { toast } = useToast();
  // Ref so the unauth callback always reads latest state without stale closure
  const tokenRef = useRef<string | null>(null);
  tokenRef.current = token;

  // On mount: restore token from sessionStorage and re-validate with server
  useEffect(() => {
    const stored = readStoredToken();
    if (!stored) { setHydrating(false); return; }

    apiRequest("GET", "/api/auth/me", undefined, stored)
      .then(r => {
        if (!r.ok) throw new Error("invalid");
        return r.json() as Promise<User>;
      })
      .then(user => {
        setActiveToken(stored);
        setToken(stored);
        setUser(user);
      })
      .catch(() => {
        // Token expired or server flushed (cold start) — clear it silently
        try { sessionStorage.removeItem(TOKEN_KEY); } catch {}
      })
      .finally(() => setHydrating(false));
  }, []);

  // Re-validate the session token when the page becomes visible again after
  // being in the background for 5+ minutes. Catches expired tokens before
  // the engineer tries to tap an action and gets a confusing 401 sign-out.
  useEffect(() => {
    let hiddenAt: number | null = null;
    const REVALIDATE_AFTER_MS = 5 * 60 * 1000;

    const onVisibilityChange = () => {
      if (document.hidden) {
        hiddenAt = Date.now();
      } else if (hiddenAt !== null && tokenRef.current) {
        const gap = Date.now() - hiddenAt;
        hiddenAt = null;
        if (gap >= REVALIDATE_AFTER_MS) {
          apiRequest("GET", "/api/auth/me")
            .then(r => r.json() as Promise<User>)
            .then(user => setUser(user))
            .catch(() => {}); // 401 path already handled by the global unauth handler
        }
      }
    };

    document.addEventListener("visibilitychange", onVisibilityChange);
    return () => document.removeEventListener("visibilitychange", onVisibilityChange);
  }, []);

  // Register a global 401 handler — fires when a stale tab tries to use an
  // invalidated token (server restart / new deploy). We sign the user out
  // and show a clear toast so they know why they're back at the login screen.
  useEffect(() => {
    const handle = () => {
      // Only act if we actually have a token — avoids double-firing on login page
      if (!tokenRef.current) return;
      try { sessionStorage.removeItem(TOKEN_KEY); } catch {}
      setActiveToken(null);
      setToken(null);
      setUser(null);
      queryClient.clear();
      toast({
        title: "Session expired",
        description: "Your session has expired — please sign in again.",
        variant: "destructive",
      });
    };
    registerUnauthHandler(handle);
    return () => unregisterUnauthHandler();
  }, [toast]);

  const loginWithToken = useCallback((tok: string, user: User) => {
    try { sessionStorage.setItem(TOKEN_KEY, tok); } catch {}
    setActiveToken(tok);
    setToken(tok);
    setUser(user);
    // Signal FeaturesProvider to reload the authenticated feature flags
    // (env-vars + admin DB toggles via /api/features)
    window.dispatchEvent(new Event("fso:login"));
  }, []);

  const logout = useCallback(() => {
    if (token) {
      // Fire-and-forget — invalidate server-side
      apiRequest("POST", "/api/auth/logout").catch(() => {});
    }
    try { sessionStorage.removeItem(TOKEN_KEY); } catch {}
    setActiveToken(null);
    setToken(null);
    setUser(null);
  }, [token]);

  return (
    <UserContext.Provider value={{ currentUser, token, loginWithToken, logout, hydrating }}>
      {children}
    </UserContext.Provider>
  );
}

export function useUser() {
  return useContext(UserContext);
}
